import { createContext, type ReactNode, useCallback, useLayoutEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { storageKeys } from '../config/storageKeys';
import AuthService from '../services/AuthService';
import { accountsHttpClient } from '../services/utils/accountsHttpClient';
import { httpClient } from '../services/utils/httpClient';

interface IAuthContextValue {
	signedIn: boolean;
	ownerId: string;
	signIn(email: string, password: string): Promise<void>;
	signOut(): void;
	setSignedIn(value: boolean): void;
}

export const AuthContext = createContext({} as IAuthContextValue);

export function AuthProvider({ children }: { children: ReactNode }) {
	const [signedIn, setSignedIn] = useState(() => {
		return !!localStorage.getItem(storageKeys.accessToken);
	});
	const [ownerId, setOwnerId] = useState(() => {
		return localStorage.getItem(storageKeys.email) ?? '';
	});

	useLayoutEffect(() => {
		const interceptorId = accountsHttpClient.interceptors.request.use((config) => {
			const accessToken = localStorage.getItem(storageKeys.accessToken);

			if (accessToken) {
				config.headers.set('Authorization', `Bearer ${accessToken}`);
			}

			return config;
		});

		return () => {
			accountsHttpClient.interceptors.request.eject(interceptorId);
		};
	}, []);

	useLayoutEffect(() => {
		const interceptorId = httpClient.interceptors.request.use((config) => {
			const accessToken = localStorage.getItem(storageKeys.accessToken);

			if (accessToken) {
				config.headers.set('Authorization', `Bearer ${accessToken}`);
			}

			return config;
		});

		return () => {
			accountsHttpClient.interceptors.request.eject(interceptorId);
		};
	}, []);

	const signIn = useCallback(async (email: string, password: string) => {
		try {
			const { accessToken, infoUser } = await AuthService.signIn({ email, password });

			localStorage.setItem(storageKeys.accessToken, accessToken);
			localStorage.setItem(storageKeys.name, infoUser.name);
			localStorage.setItem(storageKeys.email, infoUser.email);
			localStorage.setItem(storageKeys.admin, String(infoUser.adm));

			setSignedIn(true);
			setOwnerId(infoUser.email);
		} catch {
			toast.error('Credenciais inválidas!');
		}
	}, []);

	const signOut = useCallback(async () => {
		try {
			await accountsHttpClient.delete('/deactivate_token');

			localStorage.clear();
			setSignedIn(false);
		} catch {
			toast.error('Ocorreu um erro ao realizar o logout!');
		}
	}, []);

	const value: IAuthContextValue = {
		signedIn,
		ownerId,
		signIn,
		signOut,
		setSignedIn
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
